import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import ProjectsPanel from "@/components/admin/ProjectsPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function LoginCard() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper text-ink flex flex-col items-center justify-center px-5">
            <form
                data-testid="admin-login-form"
                onSubmit={submit}
                className="w-full max-w-md border border-ink/15 bg-white/60 p-8 md:p-10"
            >
                <LogoMark size={30} />
                <h1 className="mt-6 font-display font-extrabold uppercase tracking-tight text-3xl">Admin access</h1>
                <p className="mt-2 text-sm text-inkdim">Portfolio management — Hitanshu Shah</p>
                <div className="mt-8 space-y-5">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Email</label>
                        <Input
                            data-testid="admin-login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 bg-transparent border-ink/30 rounded-none"
                            placeholder="admin@hitanshushah.com"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Password</label>
                        <Input
                            data-testid="admin-login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 bg-transparent border-ink/30 rounded-none"
                            placeholder="••••••••"
                        />
                    </div>
                    <Button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={busy}
                        className="w-full rounded-none bg-ink text-paper hover:bg-inkdim uppercase text-xs font-bold tracking-[0.2em] py-4"
                    >
                        {busy ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
                    </Button>
                </div>
            </form>
            <a
                href="/"
                data-testid="admin-back-to-site"
                className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-inkdim hover:text-ink"
            >
                <ArrowLeft size={14} /> Back to site
            </a>
        </div>
    );
}

function Dashboard({ user }) {
    const { logout } = useAuth();
    const [unread, setUnread] = useState(0);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="admin-dashboard">
            <header className="border-b border-line bg-paper/85 backdrop-blur-xl sticky top-0 z-30">
                <div className="flex items-center justify-between px-5 md:px-10 h-16">
                    <div className="flex items-center gap-3">
                        <LogoMark size={24} />
                        <span className="font-display font-extrabold uppercase text-sm tracking-tight">
                            Admin — {user.email}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="text-xs font-bold uppercase tracking-[0.18em] text-inkdim hover:text-ink px-3 py-2"
                        >
                            View site
                        </a>
                        <Button
                            data-testid="admin-logout"
                            onClick={logout}
                            variant="outline"
                            className="rounded-none border-ink/30 uppercase text-xs font-bold tracking-[0.18em]"
                        >
                            Logout
                        </Button>
                    </div>
                </div>
            </header>
            <main className="px-5 md:px-10 py-10 max-w-6xl mx-auto">
                <Tabs defaultValue="projects">
                    <TabsList className="bg-transparent border-b border-line w-full justify-start rounded-none h-auto p-0 gap-6">
                        <TabsTrigger
                            data-testid="admin-tab-projects"
                            value="projects"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase text-xs font-bold tracking-[0.18em] py-3"
                        >
                            Projects
                        </TabsTrigger>
                        <TabsTrigger
                            data-testid="admin-tab-messages"
                            value="messages"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase text-xs font-bold tracking-[0.18em] py-3"
                        >
                            Messages {unread > 0 ? `(${unread})` : ""}
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="projects" className="mt-8">
                        <ProjectsPanel />
                    </TabsContent>
                    <TabsContent value="messages" className="mt-8">
                        <MessagesPanel onUnreadChange={setUnread} />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

export default function Admin() {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <Loader2 className="animate-spin text-inkdim" />
            </div>
        );
    }
    if (!user) return <LoginCard />;
    return <Dashboard user={user} />;
}
